import { GlassPanel } from '@/components/glass/GlassPanel';
import { RadiusSlider } from '@/components/dashboard/RadiusSlider';
import { CategoryToggle } from './CategoryToggle';
import { HighlightToggle } from './HighlightToggle';
import { RadiusOnlyToggle } from './RadiusOnlyToggle';
import { RadiusCirclesToggle } from './RadiusCirclesToggle';
import { AmenityPinsToggle } from './AmenityPinsToggle';
import { AMENITY_CATEGORIES, type AmenityCategory } from '@/lib/map-constants';
import type { FilterState } from '@/types/db';

export interface MapFilterBarProps {
  filters: FilterState;
  /**
   * Partial-state writer. Plan 08-02 always uses partial patches —
   * `useFilters` accepts both partial and full FilterState.
   */
  setFilters: (patch: Partial<FilterState>) => void;
  /** Total visible amenity count after the cats filter is applied. */
  amenityCount: number;
  /** Total properties (loaded from /api/map-data; not filter-aware). */
  propertyCount: number;
  /**
   * Properties currently inside any visible amenity radius — derived
   * client-side via Turf.js in `<MapPage>` (mirrors the LeafletMap
   * classification engine).
   */
  inRadiusCount: number;
}

/**
 * `<MapFilterBar>` — glass-strong sticky filter band at the top of
 * `/app/map`. Single source of map-side filter writes; shares state with
 * the dashboard via `useFilters` URL params (MAP-02).
 *
 * UI-SPEC § 4.3 layout (left → right):
 *   RadiusSlider · | · 4×CategoryToggle · | · HighlightToggle · ResultCount
 *
 * Desktop only — mobile (<md) renders `<MapFilterBarMobile>` which wraps
 * the same controls in a `<details>` disclosure (CARRY-1 pattern from
 * Phase 06).
 *
 * The result-count text is wrapped in `aria-live="polite"` so screen
 * readers announce changes after the slider settles (FILT-03 debounce
 * keeps chatter off the announcement stream).
 */
export function MapFilterBar({
  filters,
  setFilters,
  amenityCount,
  propertyCount,
  inRadiusCount,
}: MapFilterBarProps) {
  const visible = new Set<AmenityCategory>(filters.cats as AmenityCategory[]);

  const toggleCategory = (cat: AmenityCategory) => {
    const next = new Set(visible);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    // Preserve canonical AMENITY_CATEGORIES order so URL stays stable.
    const ordered = AMENITY_CATEGORIES.filter((m) => next.has(m.db)).map((m) => m.db);
    setFilters({ cats: ordered });
  };

  const toggleHighlight = () => setFilters({ highlight: !filters.highlight });
  const toggleInRadiusOnly = () =>
    setFilters({ inRadiusOnly: !filters.inRadiusOnly });
  const toggleRadiusCircles = () =>
    setFilters({ showRadiusCircles: !filters.showRadiusCircles });
  const toggleAmenityPins = () =>
    setFilters({ showAmenityPins: !filters.showAmenityPins });

  return (
    <GlassPanel
      as="section"
      variant="strong"
      role="region"
      aria-label="Map filters"
      className="sticky top-16 z-30 w-full px-6 h-14 hidden md:flex items-center gap-4"
    >
      <div className="min-w-[280px]">
        <RadiusSlider
          value={filters.radius}
          onChange={(next) => setFilters({ radius: next })}
        />
      </div>
      <div aria-hidden className="w-px h-6 bg-foam-50/10" />
      <div className="flex items-center gap-2">
        {AMENITY_CATEGORIES.map((meta) => (
          <CategoryToggle
            key={meta.db}
            meta={meta}
            active={visible.has(meta.db)}
            onToggle={() => toggleCategory(meta.db)}
          />
        ))}
      </div>
      <div aria-hidden className="w-px h-6 bg-foam-50/10" />
      <HighlightToggle active={filters.highlight} onToggle={toggleHighlight} />
      <RadiusOnlyToggle
        active={filters.inRadiusOnly}
        onToggle={toggleInRadiusOnly}
      />
      <RadiusCirclesToggle
        active={filters.showRadiusCircles}
        onToggle={toggleRadiusCircles}
      />
      <AmenityPinsToggle
        active={filters.showAmenityPins}
        onToggle={toggleAmenityPins}
      />
      <span
        aria-live="polite"
        aria-atomic="true"
        className="ml-auto text-sm text-foam-50/80 tabular-nums"
      >
        {amenityCount} amenities · {propertyCount} properties (
        {filters.highlight ? `${inRadiusCount} in radius` : '— in radius'})
      </span>
    </GlassPanel>
  );
}

/**
 * `<MapFilterBarMobile>` — `<md` variant. Same controls, wrapped in a
 * `<details open>` disclosure (matches Phase 06 CARRY-1). Tailwind
 * responsive utilities hide the desktop bar at `<md` and the mobile bar
 * at `md+`, so the page renders both unconditionally and the browser
 * picks one.
 */
export function MapFilterBarMobile({
  filters,
  setFilters,
  amenityCount,
  propertyCount,
  inRadiusCount,
}: MapFilterBarProps) {
  const visible = new Set<AmenityCategory>(filters.cats as AmenityCategory[]);

  const toggleCategory = (cat: AmenityCategory) => {
    const next = new Set(visible);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    const ordered = AMENITY_CATEGORIES.filter((m) => next.has(m.db)).map((m) => m.db);
    setFilters({ cats: ordered });
  };

  const toggleHighlight = () => setFilters({ highlight: !filters.highlight });
  const toggleInRadiusOnly = () =>
    setFilters({ inRadiusOnly: !filters.inRadiusOnly });
  const toggleRadiusCircles = () =>
    setFilters({ showRadiusCircles: !filters.showRadiusCircles });
  const toggleAmenityPins = () =>
    setFilters({ showAmenityPins: !filters.showAmenityPins });

  return (
    <details
      open
      className="md:hidden glass-strong sticky top-16 z-30 w-full"
    >
      <summary className="px-4 h-11 flex items-center justify-between text-sm font-semibold text-foam-50 cursor-pointer list-none">
        <span>
          Filters · {amenityCount} amenities ·{' '}
          {filters.highlight
            ? `${inRadiusCount} in radius`
            : `${propertyCount} properties`}
        </span>
        <span aria-hidden className="text-foam-50/60 text-xs">
          tap to toggle
        </span>
      </summary>
      <div className="px-4 py-3 flex flex-col gap-3 border-t border-foam-50/10">
        <RadiusSlider
          value={filters.radius}
          onChange={(next) => setFilters({ radius: next })}
          className="w-full"
        />
        <div className="flex flex-wrap gap-2">
          {AMENITY_CATEGORIES.map((meta) => (
            <CategoryToggle
              key={meta.db}
              meta={meta}
              active={visible.has(meta.db)}
              onToggle={() => toggleCategory(meta.db)}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <HighlightToggle
            active={filters.highlight}
            onToggle={toggleHighlight}
          />
          <RadiusOnlyToggle
            active={filters.inRadiusOnly}
            onToggle={toggleInRadiusOnly}
          />
          <RadiusCirclesToggle
            active={filters.showRadiusCircles}
            onToggle={toggleRadiusCircles}
          />
          <AmenityPinsToggle
            active={filters.showAmenityPins}
            onToggle={toggleAmenityPins}
          />
        </div>
      </div>
    </details>
  );
}
